import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, throwError } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import * as FavoritesActions from './favorites.actions';
import { FavoritesService } from '../favorites.service';
import {
    FavoriteLocation,
    FavoritePreference,
    FavoriteSchedule,
    FavoriteStop,
    FavoriteTicket
} from '../favorites.model';
import { NotificationFacade } from '../../notifications';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class FavoritesEffects {
    constructor(
        private actions$: Actions,
        private favoritesService: FavoritesService,
        private notificationFacade: NotificationFacade,
        private translateService: TranslateService
    ) {}

    getFavoriteStops$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.getFavoriteStops),
            mergeMap(() =>
                this.favoritesService.getFavoriteStops().pipe(
                    map((favoriteStops: FavoriteStop[]) => FavoritesActions.getFavoriteStopsSuccess({ favoriteStops })),
                    catchError((err) => {
                        FavoritesActions.getFavoriteStopsFailed();
                        return throwError(err);
                    })
                )
            )
        );
    });

    addFavoriteStop$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.addFavoriteStop),
            mergeMap(({ stop }) =>
                this.favoritesService.addFavoriteStop(stop).pipe(
                    map((stop) => FavoritesActions.addFavoriteStopSuccess({ favoriteStop: stop })),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    addFavoriteStops$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.addFavoriteStopsFromLocalstorage),
            mergeMap(({ favoriteStopIds }) =>
                this.favoritesService.saveFavoriteStopsFromLocalStorage(favoriteStopIds).pipe(
                    map((favoriteStops) => FavoritesActions.addFavoriteStopsFromLocalstorageSuccess({ favoriteStops })),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    updateFavoriteStops$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.updateFavoriteStop),
            mergeMap((payload) =>
                this.favoritesService.updateFavoriteStop({ stopId: payload.stopId, alias: payload.alias }).pipe(
                    map((favoriteStop: FavoriteStop) => FavoritesActions.updateFavoriteStopSuccess({ favoriteStop })),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    deleteFavoriteStop$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.deleteFavoriteStop),
            mergeMap((payload) =>
                this.favoritesService.deleteFavoriteStop(payload.stopId).pipe(
                    map(() => FavoritesActions.deleteFavoriteStopSuccess({ stopId: payload.stopId })),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    deleteFavoriteStops$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.deleteFavoriteStops),
            mergeMap((payload) =>
                forkJoin(payload.stopIds.map((stopId) => this.favoritesService.deleteFavoriteStop(stopId))).pipe(
                    map(() => FavoritesActions.deleteFavoriteStopsSuccess({ stopIds: payload.stopIds })),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    getFavoriteLocations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.getFavoriteLocations),
            mergeMap(() =>
                this.favoritesService.getFavoriteLocations().pipe(
                    map((favoriteLocations: FavoriteLocation[]) =>
                        FavoritesActions.getFavoriteLocationsSuccess({ favoriteLocations })
                    ),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    addFavoriteLocation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.addFavoriteLocation),
            mergeMap((payload) =>
                this.favoritesService
                    .addFavoriteLocation({
                        latitude: payload.latitude,
                        longitude: payload.longitude,
                        name: payload.name,
                        pointOfInterest: payload.pointOfInterest
                    })
                    .pipe(
                        map((favoriteLocation: FavoriteLocation) =>
                            FavoritesActions.addFavoriteLocationSuccess({ favoriteLocation })
                        ),
                        catchError((err) => {
                            return throwError(err);
                        })
                    )
            )
        );
    });

    addFavoriteLocations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.addFavoriteLocationsFromLocalstorage),
            mergeMap(({ favoriteLocations }) =>
                this.favoritesService.saveFavoriteLocationsFromLocalStorage(favoriteLocations).pipe(
                    map((favoriteLocations) =>
                        FavoritesActions.addFavoriteLocationsFromLocalstorageSuccess({ favoriteLocations })
                    ),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    updateFavoriteLocations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.updateFavoriteLocation),
            mergeMap((payload) =>
                this.favoritesService.updateFavoriteLocation(payload).pipe(
                    map((favoriteLocation: FavoriteLocation) =>
                        FavoritesActions.updateFavoriteLocationSuccess({ favoriteLocation })
                    ),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    deleteFavoriteLocation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.deleteFavoriteLocation),
            mergeMap((payload) =>
                this.favoritesService.deleteFavoriteLocation(payload).pipe(
                    map(() => FavoritesActions.deleteFavoriteLocationSuccess({ favoriteLocation: payload })),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    deleteFavoriteLocations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.deleteFavoriteLocations),
            mergeMap((payload) =>
                forkJoin(
                    payload.favoriteLocations.map((favoriteLocationId) =>
                        this.favoritesService.deleteFavoriteLocation(favoriteLocationId)
                    )
                ).pipe(
                    map(() =>
                        FavoritesActions.deleteFavoriteLocationsSuccess({
                            favoriteLocations: payload.favoriteLocations
                        })
                    ),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    getFavoriteTickets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.getFavoriteTickets),
            mergeMap(() =>
                this.favoritesService.getFavoriteTickets().pipe(
                    map((favoriteTickets: FavoriteTicket[]) =>
                        FavoritesActions.getFavoriteTicketsSuccess({ favoriteTickets })
                    ),
                    catchError((err) => {
                        FavoritesActions.getFavoriteTicketsFailed();
                        return throwError(err);
                    })
                )
            )
        );
    });

    addFavoriteTicket$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.addFavoriteTicket),
            mergeMap((payload) =>
                this.favoritesService
                    .addFavoriteTicket({
                        docNum: payload.docNum,
                        categoryCode: payload.categoryCode,
                        productId: payload.productId,
                        startDateStr: payload.startDateStr,
                        endDateStr: payload.endDateStr,
                        includeReturnedTickets: payload.includeReturnedTickets,
                        includeCancelledTickets: payload.includeCancelledTickets,
                        channels: payload.channels
                    })
                    .pipe(
                        map((response) => FavoritesActions.addFavoriteTicketSuccess({ favoriteTicket: response })),
                        catchError((err) => {
                            if (
                                err.status === 409 &&
                                ['FAVORITE_TICKET_LIMIT_EXCEEDED', 'FAVORITE_TICKET_ALREADY_ADDED'].includes(
                                    err.error?.message
                                )
                            ) {
                                this.notificationFacade.errorV2(this.translateService.instant(err.error.message));
                            }
                            return throwError(err);
                        })
                    )
            )
        );
    });

    deleteFavoriteTickets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.deleteFavoriteTicket),
            mergeMap((payload) =>
                forkJoin(this.favoritesService.deleteFavoriteTickets(payload.productId)).pipe(
                    map(() =>
                        FavoritesActions.deleteFavoriteTicketSuccess({
                            productId: payload.productId
                        })
                    ),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    getFavoritePreference$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.getFavoritePreference),
            mergeMap(() =>
                this.favoritesService.getFavoritePreference().pipe(
                    map((favoritePreference: FavoritePreference) => {
                        return FavoritesActions.getFavoritePreferenceSuccess({ favoritePreference });
                    }),
                    catchError((err) => {
                        FavoritesActions.getFavoritePreferenceFailed();
                        return throwError(err);
                    })
                )
            )
        );
    });

    addFavoritePreference$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.addFavoritePreference),
            mergeMap((payload) =>
                this.favoritesService
                    .addFavoritePreference({
                        preferenceType: payload.preferenceType,
                        preferenceValue: payload.preferenceValue
                    })
                    .pipe(
                        map((response) =>
                            FavoritesActions.addFavoritePreferenceSuccess({ favoritePreference: response })
                        ),
                        catchError((err) => {
                            return throwError(err);
                        })
                    )
            )
        );
    });

    deleteFavoritePreference$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.deleteFavoritePreference),
            mergeMap(() =>
                this.favoritesService.deleteFavoritePreference().pipe(
                    map(() => {
                        return FavoritesActions.deleteFavoritePreferenceSuccess();
                    }),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    getFavoriteSchedule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.getFavoriteSchedules),
            mergeMap(() =>
                this.favoritesService
                    .getFavoriteSchedule()
                    .pipe(
                        map((favoriteSchedules: FavoriteSchedule[]) =>
                            FavoritesActions.getFavoriteSchedulesSuccess({ favoriteSchedules })
                        )
                    )
            )
        );
    });

    addFavoriteSchedule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.addFavoriteSchedule),
            mergeMap(({ favoriteSchedule }) =>
                this.favoritesService.addFavoriteSchedules(favoriteSchedule).pipe(
                    map((createdFavoriteSchedule) =>
                        FavoritesActions.addFavoriteScheduleSuccess({ favoriteSchedule: createdFavoriteSchedule })
                    ),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    deleteFavoriteSchedules$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.deleteFavoriteSchedules),
            mergeMap(({ favoriteSchedules }) =>
                forkJoin(
                    favoriteSchedules.map((favoriteLocation) =>
                        this.favoritesService.deleteFavoriteSchedule(favoriteLocation)
                    )
                ).pipe(
                    map(() => FavoritesActions.deleteFavoriteSchedulesSuccess({ favoriteSchedules })),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    addFavoriteSchedulesFromLocalStorage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FavoritesActions.addFavoriteSchedulesFromLocalstorage),
            mergeMap(() =>
                this.favoritesService.saveFavoriteSchedulesFromLocalStorage().pipe(
                    map((favoriteSchedules) =>
                        FavoritesActions.addFavoriteSchedulesFromLocalstorageSuccess({ favoriteSchedules })
                    ),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });
}
